import { Dialog, useToast } from '@vallourec-ui/vallourec-ui'
import { useTranslation } from 'react-i18next'

import { useEffect, useState } from 'react'

import { ComponentsColumn, HeaderContainer, Row, StyledText } from './style'

import TextAreaInput from '@/components/TextAreaFieldInput'
import TextFieldInput from '@/components/TextFieldInput'

type BodyDefinitionCell = {
  style: string
  text: string
}

type Content = {
  modLab: string
  procLaboratoryNumber: string
  idDocJ: string
  itemOrder: string
  reportNumber: string
  laboratoryName: string
  description: string
  idDocB: string
  addressJ: string
  generalTestDetailJ: string
  addressB: string
  client: string
  bodyDefinition: BodyDefinitionCell[][]
  bodyDefinitionWidths: number[]
  generalTestDetailB: string
  headerColumnsDefinition: Array<{
    colSpan?: number
    columns?: any[]
    [key: string]: any
  }>
  remarks: string
  inspection: string
  testName: string
}

interface EditModalData {
  content: Content
  id: string
  [key: string]: any
}

interface EditModalProps {
  isOpen: boolean
  title: string
  data: EditModalData
  setIsOpen: (isOpen: boolean) => void
  onOutsideClick: () => void
  onConfirm: (payload: EditModalData) => void
}

const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  data,
  title,
  onOutsideClick,
  onConfirm,
  setIsOpen
}) => {
  const { toast } = useToast()
  const { t } = useTranslation()

  const [testName, setTestName] = useState<string>('')
  const [laboratoryName, setLaboratoryName] = useState<string>('')
  const [idDocB, setIdDocB] = useState<string>('')
  const [addressB, setAddressB] = useState<string>('')
  const [generalTestDetailB, setGeneralTestDetailB] = useState<string>('')
  const [idDocJ, setIdDocJ] = useState<string>('')
  const [addressJ, setAddressJ] = useState<string>('')
  const [generalTestDetailJ, setGeneralTestDetailJ] = useState<string>('')

  const clearModal = () => {
    setTestName('')
    setLaboratoryName('')
    setIdDocB('')
    setAddressB('')
    setGeneralTestDetailB('')
    setIdDocJ('')
    setAddressJ('')
    setGeneralTestDetailJ('')
  }

  useEffect(() => {
    if (isOpen && data) {
      setTestName(data?.testName)
      setLaboratoryName(data?.laboratoryName)
      setIdDocB(data?.idDocB)
      setAddressB(data?.addressB)
      setGeneralTestDetailB(data?.generalTestDetailB)
      setIdDocJ(data?.idDocJ)
      setAddressJ(data?.addressJ)
      setGeneralTestDetailJ(data?.generalTestDetailJ)
    }
  }, [isOpen, data])

  const handleOnSave = async () => {
    if (
      !testName ||
      !laboratoryName ||
      !idDocB ||
      !addressB ||
      !generalTestDetailB ||
      !idDocJ ||
      !addressJ ||
      !generalTestDetailJ
    ) {
      toast({
        title: t('error'),
        type: 'error',
        description: t('report.editModal.requiredFieldsError'),
      })
      return
    }

    const payload = {
      ...data,
      testName,
      laboratoryName,
      idDocB,
      addressB,
      generalTestDetailB,
      idDocJ,
      addressJ,
      generalTestDetailJ,
    }
    Object.assign(data, payload)
    onConfirm(payload)
    clearModal()
    onOutsideClick()
  }

  return (
    <Dialog.Root
      isOpen={isOpen}
      setIsOpen={() => setIsOpen(false)}
      width="60rem"
      header={<Dialog.Header title={title} hasCloseIcon={false} />}
      closeOnInteractOutside={false}
      footer={
        <Dialog.Footer
          buttonText={t('save')}
          onConfirm={handleOnSave}
          onClose={() => {
            onOutsideClick()
            clearModal()
          }}
        />
      }
    >
      <HeaderContainer>
        <Row>
          <ComponentsColumn>
            <StyledText>{t<string>('report.editModal.testName')}</StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="userFirstName"
              disabled={false}
              onChange={(event) => setTestName(event.target.value)}
              placeholder={t('report.editModal.testNamePlaceholder')}
              width="100%"
              value={testName}
            />
          </ComponentsColumn>
        </Row>
        <Row>
          <ComponentsColumn>
            <StyledText>
              {t<string>('report.editModal.laboratoryName')}
            </StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="laboratoryName"
              disabled={false}
              onChange={(event) => setLaboratoryName(event.target.value)}
              placeholder={t('report.editModal.laboratoryNamePlaceholder')}
              width="100%"
              value={laboratoryName}
            />
          </ComponentsColumn>
        </Row>
        <Row>
          <ComponentsColumn>
            <StyledText>{t<string>('report.editModal.idDocB')}</StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="idDocB"
              disabled={false}
              onChange={(event) => setIdDocB(event.target.value)}
              placeholder={t('report.editModal.idDocBPlaceholder')}
              width="100%"
              value={idDocB}
            />
          </ComponentsColumn>
        </Row>
        <Row>
          <ComponentsColumn>
            <StyledText>{t<string>('report.editModal.adressB')}</StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="adressB"
              disabled={false}
              onChange={(event) => setAddressB(event.target.value)}
              placeholder={t('report.editModal.adressBPlaceholder')}
              width="100%"
              value={addressB}
            />
          </ComponentsColumn>
        </Row>
        <Row>
          <ComponentsColumn>
            <StyledText>
              {t<string>('report.editModal.generalTestDetailsB')}
            </StyledText>
            <TextAreaInput
              fontSize="text-md"
              name="generalTestDetailsB"
              disabled={false}
              onChange={(event) => setGeneralTestDetailB(event?.target?.value)}
              placeholder={t('report.editModal.generalTestDetailsBPlaceholder')}
              width="100%"
              value={generalTestDetailB}
            />
          </ComponentsColumn>
        </Row>
        <Row>
          <ComponentsColumn>
            <StyledText>{t<string>('report.editModal.idDocJ')}</StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="idDocJ"
              disabled={false}
              onChange={(event) => setIdDocJ(event.target.value)}
              placeholder={t('report.editModal.idDocJPlaceholder')}
              width="100%"
              value={idDocJ}
            />
          </ComponentsColumn>
        </Row>
        <Row>
          <ComponentsColumn>
            <StyledText>{t<string>('report.editModal.addressJ')}</StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="addressJ"
              disabled={false}
              onChange={(event) => setAddressJ(event.target.value)}
              placeholder={t('report.editModal.addressJPlaceholder')}
              width="100%"
              value={addressJ}
            />
          </ComponentsColumn>
        </Row>
        <Row>
          <ComponentsColumn>
            <StyledText>
              {t<string>('report.editModal.generalTestDetailsJ')}
            </StyledText>
            <TextAreaInput
              fontSize="text-md"
              name="generalTestDetailsJ"
              disabled={false}
              onChange={(event) => setGeneralTestDetailJ(event?.target?.value)}
              placeholder={t('report.editModal.generalTestDetailsJPlaceholder')}
              width="100%"
              value={generalTestDetailJ}
            />
          </ComponentsColumn>
        </Row>
      </HeaderContainer>
    </Dialog.Root>
  )
}

export default EditModal
